import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';

export default function VoicePlayer({ src, isOutgoing }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setProgress(a.duration ? (a.currentTime / a.duration) * 100 : 0);
    const onMeta = () => setDuration(a.duration || 0);
    const onEnd = () => {
      setPlaying(false);
      setProgress(0);
    };
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('ended', onEnd);
    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('ended', onEnd);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play().then(() => setPlaying(true)).catch(() => {});
    }
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    a.currentTime = pct * a.duration;
  };

  const fmt = (s) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center gap-2 min-w-[180px]">
      <audio ref={audioRef} src={src} preload="metadata" />
      <button
        type="button"
        onClick={toggle}
        data-testid="voice-play-btn"
        className={cn(
          'w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition',
          isOutgoing ? 'bg-card/20 hover:bg-card/30 text-white' : 'bg-black/10 hover:bg-black/15 text-muted-foreground'
        )}
      >
        {playing ? (
          <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
            <rect x="6" y="5" width="4" height="14" rx="1" />
            <rect x="14" y="5" width="4" height="14" rx="1" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div
          className="h-1.5 rounded-full cursor-pointer"
          onClick={seek}
          style={{
            background: isOutgoing ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.1)',
          }}
        >
          <div
            className="h-full rounded-full transition-all duration-100"
            style={{
              width: `${progress}%`,
              background: isOutgoing ? 'rgba(255,255,255,0.8)' : '#506690',
            }}
          />
        </div>
        <div className={cn('text-[10px] mt-0.5', isOutgoing ? 'text-white/60' : 'text-muted-foreground')}>
          {fmt(duration)}
        </div>
      </div>
    </div>
  );
}
